import { ApolloError, Reference } from '@apollo/client';
import { getEmptyStructure } from 'lib-knowledge-document-structure/structure/empty';
import {
  ArticleCreateMutation,
  ArticleFieldsFragmentDoc,
  ArticleTranslationInput,
  Category,
  LanguageId,
  useArticleCreateMutation,
} from 'lib-knowledge-management-common/graphql';
import { ParentRef } from 'lib-knowledge-management-common/models/types';
import { ampli } from 'lib-shared-tracking';

type MutationFunction = ReturnType<typeof useArticleCreateMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type CreateArticleFn = (data: {
  title: string;
  parentObj: ParentRef;
  languageId: LanguageId;
}) => MutationReturnValue;
type UseArticleCreateReturnValue = [
  CreateArticleFn,
  ReturnType<typeof useArticleCreateMutation>[1],
];

type UseArticleCreateParams = {
  onCompleted: (data: ArticleCreateMutation) => void;
  onError: (error: ApolloError) => void;
};

function useArticleCreate({
  onCompleted,
  onError,
}: UseArticleCreateParams): UseArticleCreateReturnValue {
  const [createMutation, mutationResult] = useArticleCreateMutation({
    onCompleted,
    onError,
  });
  const create: CreateArticleFn = ({ title, parentObj, languageId }) => {
    const translations: ArticleTranslationInput[] = [
      {
        languageId,
        title,
        content: getEmptyStructure(),
      },
    ];
    return createMutation({
      variables: {
        translations,
        parentId: parentObj.id,
      },
      update(cache, { data }) {
        if (!data) return null;

        cache.modify<Category>({
          id: cache.identify(parentObj),
          fields: {
            articles(existingChildrenRefs = [], { readField }) {
              const newArticle = data.articleCreate;
              if (!newArticle) throw new Error('Created Article not returned.');

              const newArticleRef = cache.writeFragment({
                data: newArticle,
                fragment: ArticleFieldsFragmentDoc,
              }) as Reference;

              // Quick safety check - if the new comment is already
              // present in the cache, we don't need to add it again.
              if (
                existingChildrenRefs.some(
                  (ref) => readField('id', ref) === newArticle.id,
                )
              ) {
                return existingChildrenRefs;
              }
              return [...existingChildrenRefs, newArticleRef] as Reference[];
            },
          },
        });
      },
    }).then((args) => {
      ampli.knowledgeArticleCreated({
        articleId: args.data?.articleCreate?.id || 'unknown',
        source: 'management',
      });
      return args;
    });
  };

  return [create, mutationResult];
}

export default useArticleCreate;
